import { Channel } from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { usePartnerData } from '../hooks/usePartnerData';
import { MappingView } from './mapping-view';
import { MappingViewButton } from './mapping-view-button';

export interface DataCentersAndChannelsProps {
  activeTab: string | null;
  currentChannelId: number | undefined;
  loading: boolean;
  onSelectChannel: (channelId: number) => void;
  selectedChannelId: number | null;
}

export function DataCentersAndChannels({
  activeTab,
  currentChannelId,
  loading,
  onSelectChannel,
  selectedChannelId,
}: DataCentersAndChannelsProps) {
  const { channel: activeChannelParam, target: activeTargetParam } = useSearch({
    from: '/_auth/partner/mapping',
  });
  const navigate = useNavigate({ from: '/partner/mapping' });

  const [searchTerm, setSearchTerm] = useState('');

  const { dataCenters } = usePartnerData();

  const handleClickChannel = (isSelected: boolean, channel: Pick<Channel, 'id' | 'name'>) => {
    if (!activeTargetParam) {
      navigate({ search: (params) => ({ ...params, channel: isSelected ? '' : channel.name }) });
    } else {
      onSelectChannel(channel.id);
    }
  };

  return (
    <MappingView
      activeTab={activeTab}
      filtersTitle="Data Centers"
      items={dataCenters}
      panelsTitle="Channels"
      renderFilter={({ channels, name }) => {
        const channelInDataCenter = channels.some(
          (channel) => channel.id === selectedChannelId || channel.id === currentChannelId,
        );
        return (
          <>
            {name}{' '}
            {channelInDataCenter && (
              <span
                className="size-2 rounded-full bg-foreground transition-colors"
                data-testid={`${name}-channel-indicator`}
              />
            )}
          </>
        );
      }}
      renderPanel={({ channels }) => {
        const filteredChannels = channels.filter((channel) => {
          const isActive = activeChannelParam === channel.name;
          return new RegExp(searchTerm, 'gi').test(channel.name) || isActive;
        });

        return filteredChannels.map((channel) => {
          const isSelected =
            activeChannelParam === channel.name || selectedChannelId === channel.id;
          const isActive = activeChannelParam === channel.name;

          return (
            <MappingViewButton
              key={channel.name}
              ref={(ref) => {
                if (selectedChannelId === channel.id) {
                  ref?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }}
              isActive={isActive}
              loading={loading}
              pressed={isSelected}
              onClick={() => handleClickChannel(isSelected, channel as Channel)}>
              {channel.name}
            </MappingViewButton>
          );
        });
      }}
      onSearch={setSearchTerm}
    />
  );
}
